/*
 * Copyright (c) 2025, King Living
 * CRM Synchronization Hook
 *
 * Frontend hook for synchronizing customer data with Salesforce CRM.
 * Handles registration, login, and profile update scenarios.
 *
 * Features:
 * - Fail-soft approach: CRM errors don't block commerce flows
 * - Debug logging controlled by localStorage flag
 * - Support for marketing preferences sync
 *
 * Sync Retry Logic:
 * - If a CRM sync fails, the server stores the data in SFCC customer profile
 * - On next login, the server automatically retries the pending sync
 * - The frontend doesn't need to handle retry logic - it's all server-side
 *
 * Debug Mode:
 * - Set localStorage.setItem('PWA_DEBUG', 'true') to enable client-side debug logs
 * - Debug logs include request/response details and timing
 *
 * @module hooks/use-crm-sync
 */

import {useCallback, useState} from 'react';

import {useAccessToken} from '@salesforce/commerce-sdk-react';

import {fetchWithTimeout} from '@amblique/king-living/app/utils/fetch-with-timeout';

/**
 * Check if debug mode is enabled
 * Debug can be enabled via localStorage: localStorage.setItem('PWA_DEBUG', 'true')
 */
const isDebugMode = () => {
  if (typeof window === 'undefined') return false;

  try {
    return localStorage.getItem('PWA_DEBUG') === 'true';
  } catch {
    return false;
  }
};

/**
 * Debug logger for CRM operations
 */
const debugLog = (context, message, data = {}) => {
  if (!isDebugMode()) return;

  const timestamp = new Date().toISOString();

  console.log(`[CRM Debug ${timestamp}] ${context}:`, message, data);
};

/**
 * Error logger (always logs, not just in debug mode)
 */
const errorLog = (context, message, error = {}) => {
  const timestamp = new Date().toISOString();

  console.error(`[CRM Error ${timestamp}] ${context}:`, message, {
    error: error.message || error,
    stack: error.stack,
  });
};

/**
 * CRM Sync Hook
 *
 * Provides methods to synchronize customer data with Salesforce CRM.
 * The server handles retry logic for failed syncs automatically.
 *
 * @returns {Object} Hook result
 * @returns {Function} result.syncCrm - Main sync function
 * @returns {Function} result.syncRegistration - Sync after registration
 * @returns {Function} result.syncLogin - Sync on login (server auto-retries pending syncs)
 * @returns {Function} result.syncProfileUpdate - Sync profile changes
 * @returns {Function} result.getCrmCustomer - Get CRM data without sync
 * @returns {boolean} result.isLoading - Loading state
 * @returns {Object|null} result.error - Error object if sync failed
 * @returns {Object|null} result.crmData - CRM data from last sync
 * @returns {string|null} result.warning - Warning message if partial failure
 *
 * @example
 * const {syncRegistration, syncLogin, isLoading, crmData} = useCrmSync();
 *
 * // After successful registration
 * await syncRegistration({
 *   customerNo: customer.customerNo,
 *   profile: { firstName, lastName, email },
 *   acceptsMarketing: true,
 * });
 *
 * // On login (server auto-retries any pending syncs)
 * await syncLogin({ customerNo: customer.customerNo });
 */
export const useCrmSync = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [crmData, setCrmData] = useState(null);
  const [warning, setWarning] = useState(null);

  // Get SLAS access token for API calls
  const {getTokenWhenReady} = useAccessToken();

  /**
   * Core CRM sync function
   *
   * @param {Object} params - Sync parameters
   * @param {string} params.mode - Sync mode: 'register' | 'login' | 'update'
   * @param {Object} [params.profile] - Customer profile data
   * @param {Object} [params.preferences] - Marketing preferences
   * @returns {Promise<Object>} Sync result
   * @description Server uses customer_id (rcid) from SLAS token as CRM externalId.
   */
  const syncCrm = useCallback(async ({mode, profile, preferences}) => {
    const startTime = Date.now();

    setIsLoading(true);
    setError(null);
    setWarning(null);

    debugLog('syncCrm', 'Starting CRM sync', {
      mode,
      hasProfile: !!profile,
      hasPreferences: !!preferences,
    });

    try {
      // Get SLAS access token for Authorization header
      const accessToken = await getTokenWhenReady();

      if (!accessToken) {
        throw new Error('No access token available');
      }

      debugLog('syncCrm', 'Access token retrieved', {hasToken: !!accessToken});

      const requestBody = {
        mode,
        ...(profile && {profile}),
        ...(preferences && {preferences}),
      };

      debugLog('syncCrm', 'Request body', requestBody);

      // Make sync request with Authorization header
      const response = await fetchWithTimeout('/api/crm/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      const duration = Date.now() - startTime;
      const result = await response.json();

      debugLog('syncCrm', 'Response received', {
        status: response.status,
        duration: `${duration}ms`,
        ok: result.ok,
        hasCrmData: !!result.crm,
        warning: result.warning,
      });

      if (!response.ok) {
        // Non-2xx response - but still try to extract error details
        const errorMessage = result.error || `CRM sync failed with status ${response.status}`;

        throw new Error(errorMessage);
      }

      // Update state with result
      setCrmData(result.crm);

      if (result.warning) {
        setWarning(result.warning);
        console.warn('[CRM] ⚠️ Sync warning:', result.warning);
      }

      // Sync preferences if provided and CRM sync succeeded (server uses token)
      if (preferences && result.crm) {
        debugLog('syncCrm', 'Syncing preferences', preferences);

        try {
          await syncPreferences(preferences);
        } catch (prefError) {
          // Don't fail the main sync for preferences errors
          console.warn('[CRM] ⚠️ Preferences sync failed:', prefError.message);
          setWarning(result.warning || 'Preferences sync pending');
        }
      }

      console.log(`[CRM] ✅ ${mode} sync completed in ${duration}ms`);

      return {
        ok: true,
        crm: result.crm,
        warning: result.warning,
      };
    } catch (err) {
      const duration = Date.now() - startTime;

      errorLog('syncCrm', `${mode} sync failed after ${duration}ms`, err);
      setError(err);

      // Return fail-soft result - don't throw
      // Server handles storing data for retry on next login
      return {
        ok: false,
        error: err.message,
        crm: null,
      };
    } finally {
      setIsLoading(false);
    }
  }, [getTokenWhenReady]);

  /**
   * Sync marketing preferences
   *
   * @param {Object} preferences - Preferences object
   * @param {boolean} [preferences.emailOptIn] - Email marketing opt-in
   * @param {boolean} [preferences.smsOptIn] - SMS marketing opt-in
   * @param {string} [preferences.phoneNumber] - Phone number for SMS
   * @description Server uses customer_id (rcid) from SLAS token.
   */
  const syncPreferences = useCallback(async (preferences) => {
    debugLog('syncPreferences', 'Syncing preferences', preferences);

    const accessToken = await getTokenWhenReady();

    const response = await fetchWithTimeout('/api/crm/preferences', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        emailMarketing: preferences.emailOptIn,
        smsMarketing: preferences.smsOptIn,
        phone: preferences.phoneNumber,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to sync preferences');
    }

    debugLog('syncPreferences', 'Preferences synced', result);

    return result;
  }, [getTokenWhenReady]);

  /**
   * Sync after customer registration
   *
   * Called immediately after successful SFCC registration.
   * Creates Person Account in Salesforce CRM with duplicate detection.
   * If sync fails, the server stores data for automatic retry on next login.
   *
   * @param {Object} params - Registration sync parameters
   * @param {Object} params.profile - Customer profile data
   * @param {string} params.profile.firstName - First name
   * @param {string} params.profile.lastName - Last name
   * @param {string} params.profile.email - Email address
   * @param {boolean} [params.acceptsMarketing] - Marketing opt-in from registration form
   * @param {string} [params.preferredStoreId] - Preferred store ID
   * @returns {Promise<Object>} Sync result. Server uses customer_id (rcid) from SLAS token.
   */
  const syncRegistration = useCallback(async ({profile, acceptsMarketing, preferredStoreId}) => {
    console.log('[CRM] 🔄 Syncing registration to CRM...');

    debugLog('syncRegistration', 'Starting registration sync', {
      profile: profile ? Object.keys(profile) : null,
      acceptsMarketing,
      preferredStoreId,
    });

    const fullProfile = {
      ...profile,
      ...(preferredStoreId && {preferredStoreId}),
      accountType: 'Retail',
    };

    const preferences = acceptsMarketing !== undefined ? {emailOptIn: acceptsMarketing} : undefined;

    return syncCrm({
      mode: 'register',
      profile: fullProfile,
      preferences,
    });
  }, [syncCrm]);

  /**
   * Sync on customer login
   *
   * Called after successful SFCC login to hydrate CRM data.
   * The server automatically checks for pending syncs from previous failures
   * and retries them before returning the CRM data.
   *
   * IMPORTANT: Pass profile (firstName, lastName, email) to optimize cross-country
   * account creation. This avoids an extra CRM API call when the user logs in
   * from a different country site.
   *
   * @param {Object} params - Login sync parameters
   * @param {Object} [params.profile] - Customer profile from SFCC (recommended for cross-country)
   * @param {string} [params.preferredStoreId] - Preferred store ID for the current site
   * @returns {Promise<Object>} Sync result with CRM data. Server uses customer_id (rcid) from SLAS token.
   */
  const syncLogin = useCallback(async ({profile, preferredStoreId}) => {
    console.log('[CRM] 🔄 Syncing login to CRM...');

    debugLog('syncLogin', 'Starting login sync', {hasProfile: !!profile, preferredStoreId});

    const fullProfile = profile || preferredStoreId ? {
      ...profile,
      ...(preferredStoreId && {preferredStoreId}),
    } : undefined;

    return syncCrm({
      mode: 'login',
      profile: fullProfile,
    });
  }, [syncCrm]);

  /**
   * Sync profile update
   *
   * Called when customer updates their profile.
   * If sync fails, the server stores data for automatic retry on next login.
   *
   * @param {Object} params - Update sync parameters
   * @param {Object} params.profile - Updated profile data
   * @returns {Promise<Object>} Sync result. Server uses customer_id (rcid) from SLAS token.
   */
  const syncProfileUpdate = useCallback(async ({profile}) => {
    console.log('[CRM] 🔄 Syncing profile update to CRM...');

    debugLog('syncProfileUpdate', 'Starting profile update sync', {profile: profile ? Object.keys(profile) : null});

    return syncCrm({
      mode: 'update',
      profile,
    });
  }, [syncCrm]);

  /**
   * Get CRM customer data (server uses customer_id from SLAS token).
   *
   * @returns {Promise<Object>} CRM customer data
   */
  const getCrmCustomer = useCallback(async () => {
    debugLog('getCrmCustomer', 'Fetching CRM customer');

    setIsLoading(true);

    try {
      const accessToken = await getTokenWhenReady();

      const response = await fetchWithTimeout('/api/crm/customer', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to fetch CRM customer');
      }

      setCrmData(result.crm);

      if (result.warning) {
        setWarning(result.warning);
      }

      return result;
    } catch (err) {
      errorLog('getCrmCustomer', 'Failed to fetch CRM customer', err);
      setError(err);

      return {ok: false, error: err.message};
    } finally {
      setIsLoading(false);
    }
  }, [getTokenWhenReady]);

  return {
    // Methods
    syncCrm,
    syncRegistration,
    syncLogin,
    syncProfileUpdate,
    getCrmCustomer,

    // State
    isLoading,
    error,
    crmData,
    warning,

    // Utilities
    clearError: () => setError(null),
    clearWarning: () => setWarning(null),
  };
};

export default useCrmSync;
